<template>
  <div class="status-evse">

    <!-- START Search Box -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card shadow">
          <div class="card-body">
            <div class="row">
              <div class="col-lg-3">
                <b-form-group class="ct-mb-margin-bottom-100" label-class="col-form-label"
                              label="Search" label-for="evseSearchBox"
                              label-cols="3" content-cols="9">
                  <b-input-group>
                    <b-form-input id="evseSearchBox" type="search" v-model="searchForm.word" @input="resetShowDetails" />
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="col-lg-3">
                <b-form-group class="ct-mb-margin-bottom-100" label-class="col-form-label"
                              label="용량" label-for="evseSearchOutput"
                              label-cols="3" content-cols="9">
                  <multiselect id="evseSearchOutput"
                               v-model="searchForm.output" :options="outputOptions"
                               label="text" placeholder="" select-label=""
                               :allow-empty="false" deselect-label=""
                               :max-height="202" @input="search">
                    <span slot="noResult">No Result</span>
                  </multiselect>
                </b-form-group>
              </div>
              <div class="col-lg-3">
                <b-form-group class="ct-mb-margin-bottom-100" label-class="col-form-label"
                              label="상태" label-for="evseSearchStatus"
                              label-cols="3" content-cols="9">
                  <multiselect id="evseSearchStatus"
                               v-model="searchForm.status" :options="evseStatusOptions"
                               label="text" placeholder="" select-label=""
                               :allow-empty="false" deselect-label=""
                               :max-height="202" @input="search">
                    <span slot="noResult">No Result</span>
                  </multiselect>
                </b-form-group>
              </div>
              <div class="col-lg-3">
                <b-form-group label-class="col-form-label"
                              label="에러 코드" label-for="evseSearchError"
                              label-cols="3" content-cols="9">
                  <multiselect id="evseSearchError"
                               v-model="searchForm.errorCode" :options="errorCodeOptions"
                               label="text" placeholder="" select-label=""
                               :allow-empty="false" deselect-label=""
                               :max-height="202" @input="search">
                    <span slot="noResult">No Result</span>
                  </multiselect>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- END Search Box -->

    <!-- START Evse List -->
    <div class="row">
      <div class="col-lg-12">
        <div class="card shadow mb-0">
          <div class="card-body">

            <div class="row mb-3">
              <div class="col">
                <div class="float-end me-2">
                  <total-count :count="pagination.totalCount" unit="기" />
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <b-table class="datatables text-nowrap" bordered hover
                       :fields="fields" :items="items"
                       :filter="searchForm.word"
                       :filter-included-fields="filterOn"
                       :filter-debounce="150"
                       :per-page="pagination.perPage"
                       :current-page="pagination.currentPage"
                       :sort-by.sync="sortBy"
                       :sort-desc.sync="sortDesc"
                       @filtered="onFiltered"
                       @row-clicked="rowClick">

                <template #cell(connector)="data">
                  {{ getConnectorType(data.item.connector) }}
                </template>
                <template #cell(capacity)="data">
                  {{ data.item.capacity + ' kW' }}
                </template>
                <template #cell(errorCode)="data">
                  {{ $_app.code.errorCode[data.item.errorCode] }}
                </template>
                <template #cell(vendorErrorCode)="data">
                  {{ $_app.code.vendorErrorCode[pretreatVendorErrorCode(data.item.vendorErrorCode)] || '-' }}
                </template>
                <template #cell(timestamp)="data">
                  {{ $_utils.changeDateFormat(data.item.timestamp, 'YYYY-MM-DD HH:mm:ss') }}
                </template>

                <template #row-details="data">
                  <b-card class="row-detail mb-0">
                    <ul class="mb-0">
                      <li>충전소 ID : <span class="ct-text-main">{{ data.item.placeInfo.placeId }}</span></li>
                      <li>충전소 : <span class="ct-text-main">{{ data.item.placeInfo.placeName }}</span></li>
                      <li>주소 : <span class="ct-text-main">{{ data.item.placeInfo.address }}</span></li>
                      <li>상세 유형 : <span class="ct-text-main">{{ $_utils.isEmpty(data.item.placeInfo.placeType) ? '-' : $_app.code.placeType[data.item.placeInfo.placeType] }}</span></li>
                      <li>위도 : <span class="ct-text-main">{{ data.item.placeInfo.latitude }}</span></li>
                      <li>경도 : <span class="ct-text-main">{{ data.item.placeInfo.longitude }}</span></li>
                      <li>충전기 ID : <span class="ct-text-main">{{ data.item.csoEvseNum }}</span></li>
                      <li>Connector : <span class="ct-text-main">{{ getConnectorType(data.item.connector) }}</span></li>
                      <li>속도 : <span class="ct-text-main">{{ $_utils.isEmpty(data.item.speed) ? '-' : data.item.speed }}</span></li>
                      <li>용량 : <span class="ct-text-main">{{ data.item.capacity }}</span><span class="ct-text-muted"> kW</span></li>
                      <li>상태 : <span class="ct-text-main">{{ data.item.connectorStatus }}</span></li>
                      <li>에러 코드 : <span class="ct-text-main">{{ $_app.code.errorCode[data.item.errorCode] }}</span></li>
                      <li>제조사 에러 : <span class="ct-text-main">{{ $_app.code.vendorErrorCode[pretreatVendorErrorCode(data.item.vendorErrorCode)] || '-' }}</span></li>
                      <li>상태 변경 시간 : <span class="ct-text-main">{{ $_utils.changeDateFormat(data.item.timestamp, 'YYYY-MM-DD HH:mm:ss') }}</span></li>
                    </ul>
                  </b-card>
                </template>
              </b-table>
            </div>

            <div class="row mt-3">
              <div class="col mt-2">
                <b-pagination class="pagination-no-border" size="sm" align="center" v-model="pagination.currentPage"
                              first-number last-number prev-text="<" next-text=">"
                              :total-rows="pagination.totalCount"
                              :per-page="pagination.perPage"
                              @change="resetShowDetails" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- END Evse List -->

  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import TotalCount from '@/views/components/totalCount.vue';

export default {
  components: { Multiselect, TotalCount },
  data() {
    const _eApi = this.$_app.service.charger;
    return {
      evseApi: _eApi,
      searchForm: {
        word: null,
        output: { value: 'all', text: '전체' },
        status: { value: 'all', text: '전체' },
        errorCode: { value: 'all', text: '전체' }
      },
      searchJql: {
        placeInfo: {placeName: null, placeId: null}
      },
      evseStatusOptions: [],
      errorCodeOptions: [],
      outputOptions: [
        { value: 'all', text: '전체' },
        { value: '7', text: '7 kW' },
        { value: '50', text: '50 kW' },
        { value: '100', text: '100 kW' }
      ],
      fields: [
        { key: 'placeInfo.placeId', label: '충전소 ID', sortable: true, class: 'text-center' },
        { key: 'placeInfo.placeName', label: '충전소', sortable: true },
        { key: 'csoEvseNum', label: '충전기', sortable: true, class: 'text-center' },
        { key: 'connector', label: 'Connector', sortable: true, class: 'text-center' },
        { key: 'speed', label: '속도', sortable: true, class: 'text-center' },
        { key: 'capacity', label: '용량', sortable: true, class: 'text-center' },
        { key: 'connectorStatus', label: '상태', sortable: true, class: 'text-center' },
        { key: 'errorCode', label: '에러 코드', sortable: true, class: 'text-center' },
        { key: 'vendorErrorCode', label: '제조사 에러', sortable: true, class: 'text-center' },
        { key: 'timestamp', label: '상태 변경 시간', sortable: true, class: 'text-center' }
      ],
      items: [],
      sortBy: 'placeInfo.placeName',
      sortDesc: false,
      filterOn: ['placeInfo', 'csoEvseNum', 'speed'],
      pagination: {
        currentPage: 1,
        totalCount : 0,
        perPage: 10,
      }
    }
  },
  created() {
    const vm = this;

    vm.getEvseStatusOptions();
    vm.getErrorCodeOptions();
    vm.getItems();
  },
  methods: {
    getEvseStatusOptions() {
      const vm = this;
      vm.evseStatusOptions = Object.entries(vm.$_app.code.connectorStatus).map(([key, value]) => ({ value: key, text: value }));
      vm.evseStatusOptions.unshift({ value: 'all', text: '전체' });
    },
    getErrorCodeOptions() {
      const vm = this;
      vm.errorCodeOptions = Object.entries(vm.$_app.code.errorCode).map(([key, value]) => ({ value: key, text: value }));
      vm.errorCodeOptions.unshift({ value: 'all', text: '전체' });
    },
    search() {
      const vm = this;
      vm.searchJql = {
        placeInfo: {placeName: null, placeId: null}
      }

      if (!this.$_utils.isEmpty(vm.searchForm.output) && vm.searchForm.output.value !== 'all') {
        vm.searchJql['capacity'] = vm.searchForm.output.value;
      }

      if (!this.$_utils.isEmpty(vm.searchForm.status) && vm.searchForm.status.value !== 'all') {
        vm.searchJql['connectorStatus'] = vm.searchForm.status.value;
      }

      if (!this.$_utils.isEmpty(vm.searchForm.errorCode) && vm.searchForm.errorCode.value !== 'all') {
        vm.searchJql['errorCode'] = vm.searchForm.errorCode.value;
      }

      vm.$nextTick(() => {
        vm.getItems();
      });
    },
    getItems() {
      const vm = this;

      vm.evseApi.searchByExample(vm.searchJql, null, null, null)
          .then(res => {
            vm.items = res;
            vm.pagination.totalCount = res.length;
          }).catch(err => {
            console.log('Get Evse List Error :: ', err);
          });
    },
    onFiltered(filteredItems) {
      const vm = this;

      if (vm.pagination.totalCount !== filteredItems.length) {
        vm.pagination.currentPage = 1;
      }

      vm.pagination.totalCount = filteredItems.length;
    },
    rowClick(row) {
      const vm = this;

      if (row._showDetails) {
        this.$set(row, '_showDetails', false);
      } else {
        vm.items.forEach(item => {
          this.$set(item, '_showDetails', false);
        });

        vm.$nextTick(() => {
          this.$set(row, '_showDetails', true);
        });
      }
    },
    resetShowDetails() {
      const vm = this;

      vm.items.forEach(item => {
        this.$set(item, '_showDetails', false);
      });
    },
    pretreatVendorErrorCode(vendorArr) {
      if (!vendorArr) return;

      let str = vendorArr;
      str = str.replace('[', '');
      str = str.replace(']', '');

      return str.split(',')[1];
    },
    getConnectorType(type) {
      switch (type[0]) {
        case 'cType1': return 'AC 완속';
        case 'cCCS1': return 'DC 콤보';
        case 'cG105': return 'DC 차데모';
        case 's309-1P-32AA': return 'AC 3상';
        default: return '-';
      }
    }
  }
}
</script>
